/**
 * RecommendCards · AI 分类首屏推荐卡片列表（纯展示）
 *
 * 每张卡 = 图标 + 标题 + 维值标签行；末张可为多级卡（一级 `>` 二级）。建夹缺省页
 * （EmptyState）与 AI 助手新会话（ChatFeed）共用，各自在外层套 hero / 副标题 /
 * 动作行（换一批等）。
 *
 * 取组与「换一批」轮换逻辑不在本组件 —— 本组件只接收已取好组的 RecommendCardVM[]，
 * 点击某卡时回调 onSelectCard(text)。数据来自 v2 init 的 recommend_cards
 * （服务端预拼「单/单/单/多」序列），调用方经 useRecommendCards 取组后传入。
 */

import React from 'react'
import classNames from 'classnames'
import { iconTypeToAntdIcon } from '../../utils/recommendedPrompt'
import { RecommendCardVM } from './types'
import styles from './RecommendCards.module.scss'

export interface RecommendCardsProps {
  /** 已组好的推荐卡片（组卡 / 轮换在外部完成，本组件只渲染） */
  cards: RecommendCardVM[]
  /** 点击卡片 · 传卡片 text（调用方负责 sendUserMessage 等） */
  onSelectCard: (text: string) => void
  /** 是否禁用（如 SSE streaming 中） */
  disabled?: boolean
  /** 透传外层 className（父级补间距 / 约束用） */
  className?: string
}

/**
 * 单张卡的维值标签行 · 维值至多 1 行（PRD），单级 / 多级两套溢出规则不同：
 *  · 单级（values 平铺）：超出的标签「整个不展示」——.values 用 flex-wrap +
 *    一行高裁剪，容不下的标签整体换到被裁掉的第二行，不出现半截标签
 *  · 多级（level1 `>` level2）：单行、`>` 连接、超出部分「截断」——.valuesMulti
 *    用 nowrap + overflow 硬裁，末尾标签可被切断（保留 `>` 结构语义优先于完整性）
 */
function CardValues({
  card
}: {
  card: RecommendCardVM
}): React.ReactElement | null {
  if (card.multi) {
    const { level1, level2 } = card.multi
    return (
      <span className={styles.valuesMulti}>
        <span className={styles.tag}>{level1}</span>
        {level2.length > 0 && (
          <>
            <span className={styles.sep} aria-hidden="true">
              &gt;
            </span>
            {level2.map((value) => (
              <span key={value} className={styles.tag}>
                {value}
              </span>
            ))}
          </>
        )}
      </span>
    )
  }
  if (card.values && card.values.length > 0) {
    return (
      <span className={styles.values}>
        {card.values.map((value) => (
          <span key={value} className={styles.tag}>
            {value}
          </span>
        ))}
      </span>
    )
  }
  return null
}

const RecommendCards: React.FC<RecommendCardsProps> = ({
  cards,
  onSelectCard,
  disabled,
  className
}) => (
  <div
    className={classNames(styles.list, className)}
    role="list"
    aria-label="推荐分类方式"
  >
    {cards.map((card) => {
      const CardIcon = iconTypeToAntdIcon(card.iconType)
      return (
        <button
          key={card.text}
          type="button"
          role="listitem"
          className={styles.card}
          disabled={disabled}
          onClick={() => onSelectCard(card.text)}
        >
          <span className={styles.cardIcon} aria-hidden="true">
            <CardIcon />
          </span>
          <span className={styles.cardBody}>
            <span className={styles.cardTitle}>{card.title}</span>
            <CardValues card={card} />
          </span>
        </button>
      )
    })}
  </div>
)

export default RecommendCards
